import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, X } from "lucide-react";
import Tooltip from "../../tooltip/Tooltip";
import useEscapeKey from "../../../_hooks/useEscapeKey";
import "./_select.scss";

const normalizeOption = (option) =>
  typeof option === "object" && option !== null
    ? option
    : { label: option, value: option };

// Below this many options, the search box is skipped by default (design
// pattern: a short static list like Status/Environment gets a clean
// trigger, a long or dynamic one - Company, People - keeps search). One
// number, one export, so it's a single grep-and-change if it's ever wrong
// for a real screen - see `searchable` below for the per-caller override.
export const SEARCH_THRESHOLD = 8;

/**
 * Pure-UI select. `options` accepts either plain strings or {label, value}
 * objects. onChange receives the raw value, matching every other input
 * convention in this codebase.
 *
 * The dropdown is portaled to #table-action-root (same root
 * TableActionsMenu already uses, mounted in public/index.html) with a
 * JS-computed fixed position, not CSS `position: absolute` inside the
 * trigger. A Select nested in ANY scrollable/clipping ancestor (a table
 * wrapped in `overflow-x: auto`, a modal body, etc.) would otherwise get
 * its open dropdown silently clipped - portaling escapes that regardless
 * of how deep the nesting is. Outside-click-to-close checks BOTH the
 * trigger AND the portaled dropdown (not just the trigger's own
 * container), since the dropdown no longer lives inside it in the DOM.
 *
 * Shows a type-to-filter search box at the top of the open dropdown when
 * there's enough to filter (see `searchable` below for exactly when).
 * Enter selects the first filtered match (single-select only); Escape
 * always closes without selecting, from anywhere in the open panel, not
 * just while the search input has focus.
 *
 * `isMulti` (default false, fully backward-compatible - every existing
 * single-select caller is unaffected): `value`/`onChange` become an array
 * of raw values instead of one. Selecting an option toggles its membership
 * instead of replacing the value and closing the dropdown - a multi-select
 * stays open across picks, matching the standard "checklist dropdown"
 * pattern. Added to replace `@simplifyhq/simplify-ui-reusables`'
 * `FormInputMultiSelect` (no `_components/ui` equivalent existed) - a
 * genuinely reusable primitive gap, not a one-off, so extended here rather
 * than building a separate one-off multi-select component.
 *
 * `creatable` (default false, single-select only): when the search box's
 * text doesn't exactly match any existing option, an extra "Add "<text>""
 * row appears that calls `onChange` with the raw typed value - replaces
 * `FormInputSingleSelect`'s `allowNew` for callers (e.g. a custom field's
 * group name) that need "pick an existing value or type a brand new one",
 * without building a second select component just for that.
 *
 * `renderOption(option, isSelected)` (optional, backward-compatible -
 * every existing caller passes neither this nor `searchText` and gets
 * identical behavior to before): renders instead of `option.label` for
 * each dropdown row, both single and `isMulti` mode. Replaces the reusables
 * package's `renderItem` for callers needing rich rows (e.g. an avatar +
 * name + email, not just a plain label) - added for `renderInput.jsx`'s
 * `singleSelect`/`multiSelect`/`simplifyUser`/`company`/`market` cases.
 * Search filters against `option.searchText ?? option.label`, so a caller
 * can search across multiple source fields (e.g. name AND email) by
 * precomputing that string per option - `option.value` itself stays a
 * plain id (never the whole rich item) for exactly this reason: `key`s and
 * the `option.value === value` identity checks below need a stable
 * primitive, not an object a caller's own data hook might re-mint fresh on
 * every render.
 *
 * `option.disabled` (optional, per-option - every existing caller's items
 * simply don't set it, unaffected): row stays visible and searchable but
 * can't be picked. For a caller that needs to explain *why* (e.g. "this
 * value is already taken elsewhere"), pair it with `renderOption` to show
 * that reason inline rather than the option just silently not being
 * selectable.
 *
 * Async mode (optional, backward-compatible - omitting all of the below
 * leaves every existing caller's client-side-filter-a-fixed-list behavior
 * untouched): pass `onSearch` to opt in. `options` then means "the current
 * server-fetched page" rather than "the whole list to filter locally" -
 * Select stops filtering them itself and calls `onSearch(rawTypedQuery)`
 * on every keystroke instead (debouncing, if wanted, is the caller's job -
 * see `useAsyncSelectOptions`, built for exactly this).
 * - `isLoading`: shows "Loading…" in place of "No results found" while a
 *   page is in flight, or a trailing "Loading more…" row below existing
 *   options when loading a subsequent page.
 * - `hasMore`/`onLoadMore()`: an IntersectionObserver on the last rendered
 *   option calls `onLoadMore()` once it scrolls into view (mirrors
 *   `CompanySelect`'s original approach, this time disconnected on
 *   unmount).
 * - `selectedLabel`: when the current `value` isn't among the
 *   currently-fetched options (routine for an async select on Edit, before
 *   the selected item's own page has ever been fetched), shows this as the
 *   selected label instead of falling back to the raw id.
 *
 * `locked` + `badge` (both optional, backward-compatible): see TextInput's
 * identical props - a permanently system-managed field (e.g. Platform
 * Settings' "Wallet on") gets a diagonal hatch fill + dashed border instead
 * of the flat disabled gray, plus an optional "SYSTEM"-style pill next to
 * the label.
 *
 * `helper` (optional): see TextInput's identical prop - a caption line
 * between the label and the trigger, explaining what the value does.
 *
 * `searchable` (optional): overrides the default auto behavior, which
 * shows the search box only when `options.length >= SEARCH_THRESHOLD` (or
 * always, for async/`creatable` mode - see below). Pass `true`/`false` to
 * force it either way for a specific field.
 *
 * `option.group` (optional, per-option): options sharing the same `group`
 * string render under one sticky section header, matching the design's
 * "Grouped options" pattern. Assumes `options` is already sorted by group -
 * a `group` change is what starts a new header, not a lookup. Not
 * supported together with `onSearch` (a server page can straddle a group
 * boundary across requests).
 *
 * `isMulti` also renders a "N selected / Clear" footer under the option
 * list, and the trigger's chips stay on one line - once they'd overflow,
 * the rest collapse into a trailing "+N" chip instead of wrapping (a
 * ResizeObserver re-measures on trigger resize, same technique as
 * `LinkedItemsPicker`).
 *
 * `footerAction` (optional: `{ label, icon: IconComponent, onClick }`):
 * a persistent action row pinned below the scrollable option list, always
 * visible regardless of how many options there are or how far the list is
 * scrolled - unlike a plain entry appended to `options`, which gets buried
 * (and effectively undiscoverable) once enough real options exist. Mirrors
 * `CompanySelect`'s hand-built "+ Create new company" footer button for
 * callers (e.g. `MarketSelect`'s "+ Add new market") that need the same
 * always-visible inline-create affordance without hand-rolling their own
 * dropdown. Closes the dropdown before calling `onClick`, same as picking
 * a real option.
 *
 * `dense` (optional): a 28px-tall trigger for a tight inline slot (e.g. a
 * single style control next to a color swatch) - a third boolean rather
 * than folding into `compact` (32px), which already means something
 * specific (table-cell height) to existing callers.
 */
const Select = forwardRef(function Select(
  {
    label,
    value,
    options = [],
    onChange,
    disabled = false,
    required = false,
    error,
    tooltip,
    placeholder = "Select",
    compact = false,
    isMulti = false,
    creatable = false,
    renderOption,
    className = "",
    onSearch,
    isLoading = false,
    hasMore = false,
    onLoadMore,
    selectedLabel,
    emptyMessage = "No results found",
    locked = false,
    badge,
    helper,
    searchable,
    dense = false,
    footerAction,
    ...rest
  },
  ref
) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  // How many chips fit on one line before the rest collapse into a
  // trailing "+N" - starts optimistic (show every chip) so the first paint
  // never flashes an empty trigger; the layout effect below (which has no
  // dependency array, unlike this initializer) corrects it synchronously
  // before the browser paints, using whatever `value` the current render
  // actually has.
  const [visibleChipCount, setVisibleChipCount] = useState(
    isMulti ? (value || []).length : 0
  );
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const optionsRef = useRef(null);
  const searchInputRef = useRef(null);
  const chipsContainerRef = useRef(null);
  const chipsMeasureRef = useRef(null);

  const normalizedOptions = options.map(normalizeOption);
  // Async mode has no local list to filter, so it always needs the input
  // to send a query; `creatable` always needs a text field to type the new
  // value into. Otherwise: an explicit `searchable` wins, else auto by
  // count - see SEARCH_THRESHOLD above.
  const showSearchBox =
    Boolean(onSearch) || creatable || (searchable ?? normalizedOptions.length >= SEARCH_THRESHOLD);

  const openDropdown = () => {
    if (triggerRef.current) {
      // The dropdown itself is `position: fixed` (see the portaled render
      // below), which is already viewport-relative - getBoundingClientRect()
      // returns viewport-relative coordinates too, so top/left need no
      // scroll offset added. Adding window.scrollY/scrollX here double-
      // counted scroll: on any page where the window itself was scrolled
      // (not just an inner container), the dropdown rendered scrollY
      // pixels further down the viewport than the trigger, ending up
      // nowhere near it.
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setSearchQuery("");
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        !(dropdownRef.current && dropdownRef.current.contains(event.target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [isOpen]);

  // Escape used to live only on the search input's own onKeyDown, which
  // meant it did nothing once that input stopped always being there (see
  // `showSearchBox` below) - this works from anywhere in the open panel,
  // and returns focus to the trigger the way closing normally would leave
  // it after a click-driven close.
  const closeAndFocusTrigger = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);
  useEscapeKey(isOpen, closeAndFocusTrigger);

  // Autofocus the search box every time the dropdown opens with one, so
  // typing to filter works immediately without an extra click. When there's
  // no search box (see `showSearchBox` below), focus stays on the trigger.
  useEffect(() => {
    if (isOpen && showSearchBox) searchInputRef.current?.focus();
  }, [isOpen, showSearchBox]);

  // Async mode: forward every keystroke to the caller's own (typically
  // debounced) search handler instead of filtering `options` here - see
  // this component's own doc comment above.
  useEffect(() => {
    if (onSearch) onSearch(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Keep the dropdown on-screen if opening it would overflow the viewport,
  // and re-close on scroll/resize (a fixed-position portal doesn't move
  // with its trigger the way an absolutely-positioned one would) - but
  // ONLY when the scroll happened outside the dropdown itself. The option
  // list has its own internal scroll (see _select.scss's &__options), and
  // a capture-phase `scroll` listener on `window` fires for that inner
  // scroll too (capture reaches every descendant regardless of whether
  // the event bubbles) - without this guard, scrolling the option list
  // closed the dropdown on the very first scroll tick.
  useEffect(() => {
    if (!isOpen) return;
    if (dropdownRef.current && triggerRef.current) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const triggerRect = triggerRef.current.getBoundingClientRect();

      // A Select opened near the bottom of a ModalShell can have plenty of
      // *viewport* room below its trigger while the modal's own footer
      // buttons (Cancel/Submit - always visible, not part of this dropdown)
      // sit right there in that space. A window-only bounds check doesn't
      // know about them, so the dropdown opens straight over/beside them
      // with no visual gap, and the footer button bleeds out from behind
      // the dropdown's edge. Clamp the lower bound to the nearest modal
      // footer's top edge when the trigger lives inside one.
      const modalFooter = triggerRef.current
        .closest(".modal-shell__content")
        ?.querySelector(".modal-shell__footer");
      const lowerBound = modalFooter
        ? Math.min(window.innerHeight, modalFooter.getBoundingClientRect().top)
        : window.innerHeight;

      const spaceBelow = lowerBound - triggerRect.bottom;
      const spaceAbove = triggerRect.top;

      setPosition((prev) => {
        let top = prev.top;
        if (dropdownRect.height > spaceBelow && spaceAbove > spaceBelow) {
          // Doesn't fit below the trigger, but there's genuinely more room
          // above it - flip to open upward instead of clamping against the
          // viewport bottom, which would push the dropdown up into/over
          // the trigger itself whenever the window is too short for it to
          // fit below (not just when it's near the very bottom edge).
          top = triggerRect.top - dropdownRect.height - 4;
        } else if (dropdownRect.bottom > lowerBound) {
          // Neither direction has enough room - best effort, same as
          // before: pin the dropdown's bottom near the lower bound's.
          top = lowerBound - dropdownRect.height - 10;
        }

        return {
          ...prev,
          top,
          left:
            dropdownRect.right > window.innerWidth
              ? window.innerWidth - dropdownRect.width - 10
              : prev.left,
        };
      });
    }
    const close = (event) => {
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) return;
      setIsOpen(false);
    };
    // resize events target `window`, not a DOM Node - reusing `close`'s
    // dropdownRef.contains(event.target) check here threw
    // "Failed to execute 'contains' on 'Node'" on every window resize
    // while a dropdown was open, crashing the page.
    const closeOnResize = () => setIsOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", closeOnResize);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", closeOnResize);
    };
  }, [isOpen]);

  // Async mode: `options` is already the current server-filtered page -
  // filtering it again client-side against the same query would be
  // redundant at best (and wrong the instant the user has typed further
  // than what the still-in-flight page reflects).
  const filteredOptions = onSearch
    ? normalizedOptions
    : searchQuery.trim()
    ? normalizedOptions.filter((option) =>
        String(option.searchText ?? option.label ?? "")
          .toLowerCase()
          .includes(searchQuery.trim().toLowerCase())
      )
    : normalizedOptions;

  const trimmedQuery = searchQuery.trim();
  const canCreate =
    creatable &&
    !isMulti &&
    trimmedQuery.length > 0 &&
    !normalizedOptions.some((option) => String(option.label ?? "").toLowerCase() === trimmedQuery.toLowerCase());

  const handleCreate = () => {
    onChange?.(trimmedQuery);
    setIsOpen(false);
  };

  const multiValues = isMulti ? value || [] : [];
  const isOptionSelected = (option) =>
    isMulti ? multiValues.includes(option.value) : option.value === value;

  // Always reflect the current value faithfully, even if it doesn't match
  // any option (e.g. a value set before the options list existed/changed,
  // or - routine for an async select - the selected item just isn't on the
  // currently-fetched page) - falling back to the placeholder here would
  // silently hide real data. `selectedLabel` (async callers) shows the
  // item's real name in that case instead of its raw id.
  const selected = isMulti
    ? null
    : normalizedOptions.find((option) => option.value === value) ??
      (value ? { label: selectedLabel ?? value, value } : null);

  const selectedMultiLabels = isMulti
    ? multiValues.map((v) => normalizedOptions.find((option) => option.value === v)?.label ?? v)
    : [];

  // How many chips fit on one line before the rest collapse into "+N" -
  // same off-screen-measure-row technique as LinkedItemsPicker (measure
  // every chip's natural width, including its own remove button, plus a
  // worst-case "+N" suffix at the full count so there's always room
  // reserved for whatever the real overflow count turns out to be).
  //
  // Deliberately no dependency array (runs every render) - a
  // ResizeObserver re-triggers measurement purely by resetting
  // visibleChipCount to multiValues.length, an unchanged value when the
  // chip list itself didn't change, so a deps array here would silently
  // skip re-measuring after a resize. Doesn't skip while `isOpen` either -
  // the trigger's chips stay visible even with the panel open (mirrors
  // LinkedItemsPicker's own chip measurement).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const container = chipsContainerRef.current;
    const measureRow = chipsMeasureRef.current;
    if (!isMulti || !container || !measureRow || multiValues.length === 0) return;

    const containerWidth = container.clientWidth;
    const chipGap = 4; // matches .select__chips { gap: 4px }
    const chipNodes = Array.from(measureRow.querySelectorAll("[data-chip]"));
    const suffixNode = measureRow.querySelector("[data-suffix]");
    const suffixWidth = suffixNode ? suffixNode.offsetWidth : 0;

    let used = 0;
    let count = 0;
    for (let i = 0; i < chipNodes.length; i++) {
      const width = chipNodes[i].offsetWidth + (i === 0 ? 0 : chipGap);
      const hasMoreAfter = i < chipNodes.length - 1;
      const reserve = hasMoreAfter ? suffixWidth + chipGap : 0;
      if (i === 0 || used + width + reserve <= containerWidth) {
        used += width;
        count = i + 1;
      } else {
        break;
      }
    }
    setVisibleChipCount(count);
  });

  // Re-measure when the trigger's own width changes (sidebar collapse,
  // viewport resize) - resetting to the full count re-runs the layout
  // effect above against the new width on the next render.
  useEffect(() => {
    const container = chipsContainerRef.current;
    if (!isMulti || !container || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setVisibleChipCount(multiValues.length));
    observer.observe(container);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMulti]);

  const handleSelect = (option) => {
    if (option.disabled) return;
    if (isMulti) {
      const next = multiValues.includes(option.value)
        ? multiValues.filter((v) => v !== option.value)
        : [...multiValues, option.value];
      onChange?.(next);
      return;
    }
    onChange?.(option.value);
    setIsOpen(false);
  };

  // Lets a chip's own "x" remove that one value without reopening/closing
  // the dropdown - the click is stopped from bubbling to the trigger before
  // this ever runs, see the chip button's onClick below.
  const removeMultiValue = (val) => {
    onChange?.(multiValues.filter((v) => v !== val));
  };

  // The global useEscapeKey above handles Escape when focus is anywhere
  // else in the open panel, but while the search input itself has focus we
  // still need to intercept it locally: stopping propagation here keeps the
  // keydown from ever reaching document-level Escape handlers (e.g.
  // ModalShell's useEscapeKey) - without this, closing the dropdown also
  // closed the parent panel and discarded form data. Since stopping
  // propagation also prevents this Select's own document-level listener
  // from seeing the event, this branch closes the dropdown itself instead
  // of relying on that hook.
  const handleSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      closeAndFocusTrigger();
    } else if (event.key === "Enter" && filteredOptions.length > 0 && !isMulti) {
      handleSelect(filteredOptions[0]);
    } else if (event.key === "Enter" && filteredOptions.length === 0 && canCreate) {
      handleCreate();
    }
  };

  // Async mode's "load next page on scroll" - a callback ref attached to
  // the last rendered option below. Disconnects the previous observer
  // before attaching a new one (options change on every page load) and on
  // unmount - the original CompanySelect version this is modeled on never
  // disconnects on unmount. Root is the scrolling <ul> itself (optionsRef),
  // not the whole panel (dropdownRef) - the panel now also contains the
  // multiselect footer below the list, which would otherwise shrink the
  // intersection area and could fire onLoadMore early or continuously.
  const loadMoreObserverRef = useRef(null);
  const lastOptionRef = useCallback(
    (node) => {
      if (loadMoreObserverRef.current) loadMoreObserverRef.current.disconnect();
      if (node && hasMore && !isLoading && onLoadMore) {
        loadMoreObserverRef.current = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) onLoadMore();
          },
          { root: optionsRef.current }
        );
        loadMoreObserverRef.current.observe(node);
      }
    },
    [hasMore, isLoading, onLoadMore]
  );

  useEffect(() => {
    return () => loadMoreObserverRef.current?.disconnect();
  }, []);

  // Label always renders above the trigger now - see TextInput's identical
  // comment. `compact` (table cells) and `dense` (a tight inline slot) both
  // skip it entirely, same reasoning.
  const showLabel = Boolean(label) && !compact && !dense;
  // Matches TextInput's identical displayLabel pattern - see its own
  // comment for why this needs to be a visible signal, not just a
  // post-submit error.
  const displayLabel = required && label ? `${label} *` : label;

  const portalRoot = typeof document !== "undefined" && document.getElementById("table-action-root");

  // Grouped rendering: a plain flat map, with a section-header <li> spliced
  // in whenever `group` changes from the previous option - see the
  // `option.group` doc comment above for why options are assumed
  // pre-sorted by group rather than this doing any grouping itself.
  const optionRows = [];
  let lastGroup;
  filteredOptions.forEach((option, index) => {
    if (option.group && option.group !== lastGroup) {
      optionRows.push(
        <li key={`group-${option.group}`} className="select__group-label">
          {option.group}
        </li>
      );
      lastGroup = option.group;
    }
    const isLast = index === filteredOptions.length - 1;
    optionRows.push(
      <li key={option.value} ref={isLast ? lastOptionRef : null}>
        <button
          type="button"
          className={`select__option ${isMulti ? "select__option--multi" : ""} ${
            isOptionSelected(option) ? "select__option--selected" : ""
          } ${option.disabled ? "select__option--disabled" : ""}`}
          onClick={() => handleSelect(option)}
          aria-disabled={option.disabled || undefined}
        >
          {isMulti && (
            <span className={`select__checkbox ${isOptionSelected(option) ? "select__checkbox--checked" : ""}`} aria-hidden="true">
              {isOptionSelected(option) && <Check size={12} />}
            </span>
          )}
          {renderOption ? renderOption(option, isOptionSelected(option)) : option.label}
        </button>
      </li>
    );
  });

  return (
    <Tooltip content={tooltip} disabled={!tooltip} className="select-tooltip-anchor">
      <div
        ref={containerRef}
        className={`select ${error ? "select--error" : ""} ${
          locked ? "select--locked" : disabled ? "select--disabled" : ""
        } ${compact ? "select--compact" : ""} ${dense ? "select--dense" : ""} ${className}`}
      >
        {showLabel && (
          <div className="select__label-row">
            <label className="select__label">{displayLabel}</label>
            {badge && <span className="select__badge">{badge}</span>}
          </div>
        )}
        {showLabel && helper && <span className="select__helper">{helper}</span>}
        <div
          ref={(node) => {
            triggerRef.current = node;
            if (typeof ref === "function") ref(node);
            else if (ref) ref.current = node;
          }}
          // A plain `<button>` can't host the per-chip "x" `<button>`s below
          // (nested buttons are invalid HTML and browsers hoist/break their
          // click handling) - `role="button"` + the tabIndex/onKeyDown below
          // reproduce native button semantics on a div instead.
          role="button"
          className="select__trigger"
          onClick={() => !disabled && (isOpen ? setIsOpen(false) : openDropdown())}
          onKeyDown={(event) => {
            if (disabled) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              isOpen ? setIsOpen(false) : openDropdown();
            }
          }}
          // Deliberately not the native `disabled` attribute - see
          // TextInput/index.jsx for why: it suppresses mouse events almost
          // entirely, which breaks the composed Tooltip's hover handlers.
          // The onClick guard above already prevents opening the dropdown;
          // aria-disabled + tabIndex keep it out of the a11y/tab flow.
          aria-disabled={disabled || undefined}
          tabIndex={disabled ? -1 : 0}
          {...rest}
        >
          <span className="select__trigger-text" ref={chipsContainerRef}>
            {isMulti ? (
              selectedMultiLabels.length === 0 ? (
                <span className="select__placeholder">{compact ? label || placeholder : placeholder}</span>
              ) : (
                <span className="select__chips">
                  {multiValues.slice(0, visibleChipCount).map((v, index) => (
                    <span className="select__chip" key={v}>
                      <span className="select__chip-label">{selectedMultiLabels[index]}</span>
                      <button
                        type="button"
                        className="select__chip-remove"
                        aria-label={`Remove ${selectedMultiLabels[index]}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          removeMultiValue(v);
                        }}
                      >
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                  {visibleChipCount < multiValues.length && (
                    <span className="select__chip select__chip--overflow">
                      +{multiValues.length - visibleChipCount}
                    </span>
                  )}
                  {/* Off-screen, holds every chip (not just the visible
                      slice) at natural width purely so the layout effect
                      above can measure them - see LinkedItemsPicker's
                      identical &__measure-row for the same technique. */}
                  <span className="select__measure-row" ref={chipsMeasureRef} aria-hidden="true">
                    {multiValues.map((v, index) => (
                      <span className="select__chip" key={v} data-chip>
                        <span className="select__chip-label">{selectedMultiLabels[index]}</span>
                        <span className="select__chip-remove">
                          <X size={10} />
                        </span>
                      </span>
                    ))}
                    <span className="select__chip select__chip--overflow" data-suffix>
                      +{multiValues.length}
                    </span>
                  </span>
                </span>
              )
            ) : (
              <span className={selected ? "select__value" : "select__placeholder"}>
                {selected ? selected.label : compact ? label || placeholder : placeholder}
              </span>
            )}
          </span>
          {/* A locked field is structurally uneditable, not just a closed
              dropdown - the chevron affordance would falsely invite a click
              (design's own "Wallet on" chip carries no chevron either). */}
          {!locked && <ChevronDown size={14} className="select__chevron" />}
        </div>

        {isOpen &&
          portalRoot &&
          createPortal(
            <div
              ref={dropdownRef}
              className="select__dropdown select__dropdown--portaled"
              style={{
                position: "fixed",
                top: position.top,
                left: position.left,
                minWidth: position.width,
              }}
            >
              {showSearchBox && (
                <div className="select__search">
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="select__search-input"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    onKeyDown={handleSearchKeyDown}
                  />
                </div>
              )}
              <ul className="select__options" ref={optionsRef}>
                {filteredOptions.length === 0 && !canCreate ? (
                  <li className="select__empty">{isLoading ? "Loading…" : emptyMessage}</li>
                ) : (
                  optionRows
                )}
                {isLoading && filteredOptions.length > 0 && <li className="select__loading-more">Loading more…</li>}
                {canCreate && (
                  <li>
                    <button type="button" className="select__option select__option--create" onClick={handleCreate}>
                      Add "{trimmedQuery}"
                    </button>
                  </li>
                )}
              </ul>
              {isMulti && (
                <div className="select__footer">
                  <span className="select__footer-count">{multiValues.length} selected</span>
                  <button
                    type="button"
                    className="select__footer-clear"
                    onClick={() => onChange?.([])}
                    disabled={multiValues.length === 0}
                  >
                    Clear
                  </button>
                </div>
              )}
              {footerAction && (
                <button
                  type="button"
                  className="select__create-footer"
                  onClick={() => {
                    setIsOpen(false);
                    footerAction.onClick();
                  }}
                >
                  {footerAction.icon && <footerAction.icon size={14} />}
                  <span>{footerAction.label}</span>
                </button>
              )}
            </div>,
            portalRoot
          )}

        {error && <p className="select__error">{error}</p>}
      </div>
    </Tooltip>
  );
});

export default Select;
